import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipButton } from "components/common/TooltipButton";
import { navigatorButtonClasses } from "components/common/navigatorStyles";
import { COUNT_TEXT } from "components/common/SectionRule";
import { Tooltip } from "components/common/Tooltip";

export interface SettingsSection<S extends string> {
  id: S;
  label: string;
  adornment?: React.ReactNode;
}

/// The section lives in the hash so a reload lands where you were; one this
/// subject doesn't have falls back to the first rather than naming a blank page.
export const useSettingsSection = <S extends string>(
  sections: SettingsSection<S>[],
  ready: boolean,
): [S, (next: S) => void] => {
  const location = useLocation();
  const navigate = useNavigate();
  const fromHash = location.hash.replace("#", "");
  const section = sections.find((s) => s.id === fromHash)?.id ?? sections[0].id;
  useEffect(() => {
    if (ready && location.hash && location.hash !== `#${section}`)
      navigate(`#${section}`, { replace: true });
  }, [ready, location.hash, section, navigate]);
  return [section, (next) => navigate(`#${next}`, { replace: true })];
};

/// Org and space settings share this frame, so moving between them moves nothing
/// but the content. Account settings are the avatar's card, never this page.
export const SettingsFrame = <S extends string>({
  crumbs,
  actions,
  closeLabel,
  onClose,
  navLabel,
  sections,
  section,
  onSection,
  children,
}: {
  crumbs: {
    label: React.ReactNode;
    icon?: React.ReactNode;
    onClick: () => void;
  }[];
  actions?: React.ReactNode;
  closeLabel: string;
  onClose: () => void;
  navLabel: string;
  sections: SettingsSection<S>[];
  section: S;
  onSection: (next: S) => void;
  children: React.ReactNode;
}) => (
  <div className="flex h-full flex-col overflow-y-auto px-8">
    {/* Puts the crumbs level with the sidebar's Notifications row, as every list page's first row is. */}
    <div className="shrink-0" style={{ height: "4rem" }} />
    <div className="sticky top-0 z-10 shrink-0 bg-bg">
      <span className="mx-auto flex w-full max-w-3xl items-center gap-2 item-height">
        {crumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            <button
              type="button"
              onClick={crumb.onClick}
              className={`${navigatorButtonClasses} min-w-0 cursor-pointer gap-1.5 text-fg-muted-semibold`}
            >
              {crumb.icon}
              <span className="truncate">{crumb.label}</span>
            </button>
            <span className="flex items-center text-fg-muted-regular">/</span>
          </React.Fragment>
        ))}
        <span className="flex items-center px-2 item-height text-fg-semibold">
          Settings
        </span>
        <span className="ml-auto flex items-center">
          {actions}
          <TooltipButton
            title="Close settings"
            aria-label={closeLabel}
            variant="default"
            size="xs"
            onClick={onClose}
          >
            <CloseIcon fontSize="inherit" />
          </TooltipButton>
        </span>
      </span>
    </div>

    <div className="mx-auto flex w-full max-w-3xl flex-col pt-6 pb-6">
      <div className="flex gap-6">
        <nav
          aria-label={navLabel}
          className="flex w-44 shrink-0 flex-col gap-px self-start"
        >
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              aria-current={s.id === section}
              onClick={() => onSection(s.id)}
              className={`flex cursor-pointer items-center gap-2 rounded-app px-2 text-left item-height ${
                s.id === section
                  ? "bg-selected-light text-fg-medium"
                  : "text-fg-muted-medium hover:bg-list-hover hover:text-fg"
              }`}
              style={
                s.id === section
                  ? { boxShadow: "inset 2px 0 0 var(--color-accent)" }
                  : undefined
              }
            >
              <span className="flex-1">{s.label}</span>
              {s.adornment}
            </button>
          ))}
        </nav>
        <div className="flex min-w-0 flex-1 flex-col gap-6">{children}</div>
      </div>
    </div>
  </div>
);

/// A count against a menu item, tabular so it doesn't jitter.
export const SectionCount: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? <span className={COUNT_TEXT}>{count}</span> : null;

/// A destructive act as one box that warms to red on hover, the card's
/// "Delete account" row in page form: no Danger rule needed above it.
export const DangerLink: React.FC<{
  label: string;
  /// A state the box is in (a scheduled deletion), never what the act does:
  /// that is the dialog's to say.
  detail?: React.ReactNode;
  onClick: () => void;
  /// Why it cannot be pressed; the box stays in place, quiet, and says so.
  refusal?: string;
}> = ({ label, detail, onClick, refusal }) => {
  const box = (
    <button
      type="button"
      className="danger-link"
      disabled={!!refusal}
      onClick={onClick}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="font-medium" style={{ fontSize: "var(--font-size)" }}>
          {label}
        </span>
        {detail && <span className="text-small">{detail}</span>}
      </span>
      <ChevronRightIcon
        className="self-center"
        sx={{ fontSize: 16 }}
        aria-hidden
      />
    </button>
  );
  return refusal ? (
    <Tooltip title={refusal}>
      <span className="flex">{box}</span>
    </Tooltip>
  ) : (
    box
  );
};
